
import { db } from '../firebase';
import { 
    collection, 
    addDoc, 
    updateDoc, 
    doc, 
    serverTimestamp, 
    getDoc, 
    deleteDoc, 
    query, 
    where, 
    onSnapshot 
} from 'firebase/firestore';
import { Task, TaskStatus, ExternalTask, TaskComment } from '../types';
import { FirestoreUtils } from './FirestoreUtils';

export const TaskService = {
    /**
     * Subscribes to internal tasks for an organization.
     */
    listenToTasks(orgId: string, callback: (tasks: any[]) => void) {
        const q = query(collection(db, 'tasks'), where('orgId', '==', orgId));
        return onSnapshot(q, (snapshot) => {
            callback(snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                syncing: doc.metadata.hasPendingWrites
            })));
        });
    },

    /**
     * Subscribes to project team mapping for active teams.
     * Refactored: Added orgId scope and chunking for scalability.
     */
    listenToProjectTeamMap(orgId: string, teamIds: string[], callback: (map: Record<string, string>) => void) {
        if (!teamIds || teamIds.length === 0) {
            callback({});
            return () => {};
        }

        const baseQuery = query(collection(db, 'projectTeamMap'), where('orgId', '==', orgId));
        
        return FirestoreUtils.onSnapshotChunked(baseQuery, 'standUpTeamId', teamIds, (results) => {
            const map: Record<string, string> = {};
            results.forEach(data => {
                const key = `${data.provider}_${data.projectId}`;
                map[key] = data.standUpTeamId;
            });
            callback(map);
        });
    },

    /**
     * Subscribes to external tasks for active projects.
     * Refactored: Added orgId scope and chunking for scalability.
     */
    listenToExternalTasks(orgId: string, projectKeys: string[], callback: (tasks: ExternalTask[]) => void) {
        if (!projectKeys || projectKeys.length === 0) {
            callback([]);
            return () => {};
        }

        const baseQuery = query(collection(db, 'externalTasks'), where('orgId', '==', orgId));
        
        return FirestoreUtils.onSnapshotChunked(baseQuery, 'projectKey', projectKeys, (results) => {
            callback(results as ExternalTask[]);
        });
    },

    async addTask(taskData: any, userId: string) {
        const taskTitle = taskData.title;
        console.log(`[Firestore] Adding new task: "${taskTitle}"`);
        return addDoc(collection(db, 'tasks'), {
            ...taskData,
            updatedBy: userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        })
        .then((res) => {
            console.log(`[Firestore] Successfully added task: "${taskTitle}"`);
            return res;
        })
        .catch(err => {
            console.error(`[Firestore Error] Failed to add task "${taskTitle}":`, err);
            throw err;
        });
    },

    async updateTask(taskId: string, data: Partial<Task>, userId: string, taskTitle: string) {
        console.log(`[Firestore] Updating task "${taskTitle}" (${taskId}):`, data);
        const taskRef = doc(db, 'tasks', taskId);
        return updateDoc(taskRef, {
            ...data,
            updatedBy: userId,
            updatedAt: serverTimestamp()
        })
        .then((res) => {
            console.log(`[Firestore] Successfully updated task: "${taskTitle}"`);
            return res;
        })
        .catch(err => {
            console.error(`[Firestore Error] Failed to update task "${taskTitle}":`, err);
            throw err;
        });
    },

    async updateTaskStatus(taskId: string, status: TaskStatus, userId: string, taskTitle: string) {
        console.log(`[Firestore] Changing status of "${taskTitle}" to "${status}"`);
        const taskRef = doc(db, 'tasks', taskId);
        const updates: any = {
            status,
            updatedBy: userId,
            updatedAt: serverTimestamp()
        };

        try {
            const taskDoc = await getDoc(taskRef);
            if (taskDoc.exists()) {
                const currentTask = taskDoc.data();
                if ((status === 'in-progress' || status === 'deep-work') && !currentTask.startedAt) {
                    updates.startedAt = serverTimestamp();
                }
                if (status === 'done') {
                    updates.completedAt = serverTimestamp();
                } else if (currentTask.completedAt) {
                    updates.completedAt = null;
                }
            }
        } catch (e) { 
            console.error("[Firestore Error] Status update pre-check failed:", e); 
        }

        return updateDoc(taskRef, updates)
        .then((res) => {
            console.log(`[Firestore] Successfully updated status for "${taskTitle}"`);
            return res;
        })
        .catch(err => {
            console.error(`[Firestore Error] Failed to update status for "${taskTitle}":`, err);
            throw err;
        });
    },

    async deleteTask(taskId: string, taskTitle: string) {
        console.log(`[Firestore] Deleting task "${taskTitle}"`);
        return deleteDoc(doc(db, 'tasks', taskId))
        .then((res) => {
            console.log(`[Firestore] Successfully deleted task: "${taskTitle}"`);
            return res;
        })
        .catch(err => {
            console.error(`[Firestore Error] Failed to delete task "${taskTitle}":`, err);
            throw err;
        });
    },

    async addComment(taskId: string, isExternal: boolean, comment: TaskComment, taskTitle: string) {
        console.log(`[Firestore] Adding note to "${taskTitle}"`);
        const colName = isExternal ? 'externalTasks' : 'tasks';
        const taskRef = doc(db, colName, taskId);

        try {
            const taskDoc = await getDoc(taskRef);
            if (taskDoc.exists()) {
                const currentComments = taskDoc.data().comments || [];
                await updateDoc(taskRef, {
                    comments: [...currentComments, comment],
                    updatedAt: serverTimestamp()
                });
                console.log(`[Firestore] Successfully added note to "${taskTitle}"`);
            }
        } catch (e) {
            console.error(`[Firestore Error] Failed to add note to "${taskTitle}":`, e);
            throw e;
        }
    },

    async deleteComment(taskId: string, isExternal: boolean, commentId: string, taskTitle: string) {
        console.log(`[Firestore] Deleting note from "${taskTitle}"`);
        const colName = isExternal ? 'externalTasks' : 'tasks';
        const taskRef = doc(db, colName, taskId);

        try {
            const taskDoc = await getDoc(taskRef);
            if (taskDoc.exists()) {
                const currentComments = taskDoc.data().comments || [];
                const updatedComments = currentComments.filter((c: any) => c.id !== commentId);
                await updateDoc(taskRef, {
                    comments: updatedComments,
                    updatedAt: serverTimestamp()
                });
                console.log(`[Firestore] Successfully deleted note from "${taskTitle}"`);
            }
        } catch (e) {
            console.error(`[Firestore Error] Failed to delete note from "${taskTitle}":`, e);
            throw e;
        }
    }
};
